import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, effect, inject, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom, finalize } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinner } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';

import { PermissaoService } from '@/core/api/sicom-api/portal/permissao/permissao.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { DelegaciaLinha, DelegaciasService, DelegaciaSalvarRequest } from '@/features/registro/orgaos/dashboard-orgao/components/delegacias/delegacias.service';
import { ORGAO_ROUTES } from '@/features/registro/orgaos/orgao.constants';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { CardContent } from '@/shared/components/card-content/card-content';
import { LayoutService } from '@/layout/service/layout.service';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
@Component({
    selector: 'app-delegacias',
    standalone: true,
    imports: [DialogoPadraoDirective, CommonModule, ReactiveFormsModule, ButtonModule, ProgressSpinner, TagModule, CardContent, CustomTableComponent, DialogModule, InputTextModule, InputMaskModule, SelectModule, ConfirmDialogModule, CanDirective],
    providers: [ConfirmationService, MessageService],
    templateUrl: './delegacias.html',
    styleUrl: './delegacias.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class Delegacias implements OnInit {
    uf = input<string>('DF');
    dadosAtualizados = output<void>();

    protected readonly PERMISSOES = PERMISSOES;

    private readonly router = inject(Router);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly fb = inject(FormBuilder);
    private readonly inicializado = signal(false);
    private readonly permissaoService = inject(PermissaoService);
    private readonly delegaciasService = inject(DelegaciasService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly messageService = inject(MessageService);

    delegacias: DelegaciaLinha[] = [];
    carregandoLista = false;
    displayDelegaciaDialog = false;
    salvandoDelegacia = false;
    modoCadastroDelegacia = false;
    temPermissaoEdicao = signal(false);

    readonly situacoes = [
        { label: 'Ativo', value: 'Ativo' },
        { label: 'Inativo', value: 'Inativo' }
    ];

    readonly formDelegacia = this.fb.group({
        id: [null as number | null],
        uf: [{ value: '', disabled: true }, Validators.required],
        descricao: ['', Validators.required],
        sigla: ['', Validators.required],
        registro: ['', Validators.required],
        situacao: ['Ativo', Validators.required]
    });

    readonly delegaciasMetadata = computed<TableMetadata>(() => ({
        headers: {
            delegacia: { type: 'text', value: 'Delegacia', hidden: false, default: '-' },
            sigla: { type: 'text', value: 'Sigla', hidden: false, default: '-' },
            cnpj: { type: 'text', value: 'CNPJ', hidden: false, default: '-' },
            situacao: { type: 'text', value: 'Situação', hidden: false, default: '-' }
        },
        title: 'delegacia',
        actions: {
            editar: {
                label: 'Editar',
                icon: 'pi pi-pencil',
                color: 'primary',
                permission: PERMISSOES.orgaos.delegacias.editar,
                command: (item: DelegaciaLinha) => void this.abrirEdicaoDelegacia(item)
            },
            excluir: {
                label: 'Excluir',
                icon: 'pi pi-trash',
                color: 'danger',
                permission: PERMISSOES.orgaos.delegacias.excluir,
                command: (item: DelegaciaLinha) => void this.confirmarExclusaoDelegacia(item)
            },
            visualizar: {
                label: 'Visualizar',
                icon: 'pi pi-eye',
                color: 'secondary',
                command: (item: DelegaciaLinha) => this.selecionarDelegacia(item)
            }
        }
    }));

    readonly delegaciasColumnOrder = ['delegacia', 'sigla', 'cnpj', 'situacao'];
    private readonly layoutService = inject(LayoutService);
    readonly initialViewMode = computed<'table' | 'card'>(() => this.layoutService.getPresentationType());

    constructor() {
        effect(() => {
            if (!this.inicializado()) {
                return;
            }
            this.uf();
            this.carregarListaDelegacias();
        });

        effect(() => {
            const ufAtual = (this.uf() || '').trim().toUpperCase();
            if (!ufAtual) {
                this.temPermissaoEdicao.set(false);
                return;
            }
            this.permissaoService
                .verificar('mesmaUf', ufAtual)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((pode) => this.temPermissaoEdicao.set(pode));
        });
    }

    ngOnInit(): void {
        this.inicializado.set(true);
    }

    selecionarDelegacia(delegacia: DelegaciaLinha): void {
        const ufConsulta = (delegacia.uf || this.uf() || '').trim().toUpperCase();
        if (!delegacia.id || !ufConsulta) {
            return;
        }
        this.router.navigate(['/', ORGAO_ROUTES.ROOT, ORGAO_ROUTES.DELEGACIA, delegacia.id, ufConsulta]);
    }

    carregamentoInicialListaDelegacias(): boolean {
        return this.carregandoLista && this.delegacias.length === 0;
    }

    private carregarListaDelegacias(onLoaded?: () => void): void {
        const ufSelecionada = (this.uf() || '').trim().toUpperCase();
        if (!ufSelecionada) {
            this.delegacias = [];
            return;
        }

        this.carregandoLista = true;
        this.delegaciasService
            .listarPorUf(ufSelecionada)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (delegacias) => {
                    this.delegacias = delegacias;
                    this.carregandoLista = false;
                    this.cdr.markForCheck();
                    if (onLoaded) {
                        onLoaded();
                    }
                },
                error: () => {
                    this.delegacias = [];
                    this.carregandoLista = false;
                    this.cdr.markForCheck();
                }
            });
    }

    async abrirCadastroDelegacia(): Promise<void> {
        const ufAtual = (this.uf() || '').trim().toUpperCase();
        if (!(await this.podeAlterarUf(ufAtual))) {
            this.notificarSemPermissao();
            return;
        }

        this.modoCadastroDelegacia = true;
        this.formDelegacia.reset({
            id: null,
            uf: ufAtual,
            descricao: '',
            sigla: '',
            registro: '',
            situacao: 'Ativo'
        });
        this.displayDelegaciaDialog = true;
        this.cdr.markForCheck();
    }

    async abrirEdicaoDelegacia(item: DelegaciaLinha): Promise<void> {
        const ufAtual = (item.uf || this.uf() || '').trim().toUpperCase();
        if (!(await this.podeAlterarUf(ufAtual))) {
            this.notificarSemPermissao();
            return;
        }

        this.modoCadastroDelegacia = false;
        this.formDelegacia.reset({
            id: item.id,
            uf: ufAtual,
            descricao: item.delegacia || '',
            sigla: item.sigla === '-' ? '' : item.sigla,
            registro: this.formatarCnpj(item.cnpj),
            situacao: this.situacaoParaFormulario(item.situacao)
        });
        this.displayDelegaciaDialog = true;
        this.cdr.markForCheck();
    }

    async salvarDelegacia(): Promise<void> {
        if (this.formDelegacia.invalid) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Preencha os campos obrigatórios da delegacia.' });
            return;
        }

        const ufAtual = (this.formDelegacia.get('uf')?.value || '').toString().trim().toUpperCase();
        if (!(await this.podeAlterarUf(ufAtual))) {
            this.notificarSemPermissao();
            return;
        }

        const payload: DelegaciaSalvarRequest = {
            id: this.formDelegacia.get('id')?.value || undefined,
            uf: ufAtual,
            descricao: (this.formDelegacia.get('descricao')?.value || '').toString().trim(),
            sigla: (this.formDelegacia.get('sigla')?.value || '').toString().trim().toUpperCase(),
            registro: (this.formDelegacia.get('registro')?.value || '').toString(),
            situacao: (this.formDelegacia.get('situacao')?.value || 'Ativo') as 'Ativo' | 'Inativo'
        };

        this.salvandoDelegacia = true;
        this.delegaciasService
            .salvarDelegacia(payload)
            .pipe(
                finalize(() => {
                    this.salvandoDelegacia = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Sucesso',
                        detail: this.modoCadastroDelegacia ? 'Delegacia adicionada com sucesso.' : 'Delegacia atualizada com sucesso.'
                    });
                    this.displayDelegaciaDialog = false;
                    this.carregarListaDelegacias();
                    this.emitirDadosAtualizados();
                },
                error: (err) => {
                    const mensagem = err instanceof Error ? err.message : 'Erro ao salvar delegacia.';
                    this.messageService.add({ severity: 'error', summary: 'Erro', detail: mensagem });
                }
            });
    }

    async confirmarExclusaoDelegacia(item: DelegaciaLinha): Promise<void> {
        const ufAtual = (item.uf || this.uf() || '').trim().toUpperCase();
        if (!(await this.podeAlterarUf(ufAtual))) {
            this.notificarSemPermissao();
            return;
        }

        this.confirmationService.confirm({
            message: `Deseja realmente excluir a delegacia ${item.delegacia}?`,
            header: 'Excluir Delegacia',
            icon: 'pi pi-trash',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            accept: () => {
                this.delegaciasService.excluirDelegacia(item.id, ufAtual).subscribe({
                    next: () => {
                        this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Delegacia excluída com sucesso.' });
                        this.carregarListaDelegacias();
                        this.emitirDadosAtualizados();
                    },
                    error: (err) => {
                        const mensagem = err instanceof Error ? err.message : 'Erro ao excluir delegacia.';
                        this.messageService.add({ severity: 'error', summary: 'Erro', detail: mensagem });
                    }
                });
            }
        });
    }

    private situacaoParaFormulario(situacao: string): 'Ativo' | 'Inativo' {
        const valor = (situacao || '').toString().trim().toUpperCase();
        if (valor === 'FUNCIONANDO' || valor === 'ATIVO' || valor === 'A') {
            return 'Ativo';
        }
        return 'Inativo';
    }

    private formatarCnpj(valor: string): string {
        const digitos = (valor || '').toString().replace(/\D/g, '');
        if (digitos.length !== 14) {
            return digitos;
        }
        return `${digitos.slice(0, 2)}.${digitos.slice(2, 5)}.${digitos.slice(5, 8)}/${digitos.slice(8, 12)}-${digitos.slice(12)}`;
    }

    private async podeAlterarUf(uf: string): Promise<boolean> {
        if (!uf) {
            return false;
        }
        try {
            return await firstValueFrom(this.permissaoService.verificar('mesmaUf', uf));
        } catch {
            return false;
        }
    }

    private notificarSemPermissao(): void {
        this.messageService.add({
            severity: 'warn',
            summary: 'Atenção',
            detail: 'Você não possui permissão para alterar os dados desta UF.'
        });
    }

    get tituloDialogDelegacia(): string {
        return this.modoCadastroDelegacia ? 'Adicionar Delegacia' : 'Editar Delegacia';
    }

    get labelBotaoSalvarDelegacia(): string {
        return this.modoCadastroDelegacia ? 'Adicionar' : 'Salvar';
    }

    private emitirDadosAtualizados(): void {
        this.dadosAtualizados.emit();
    }
}
